import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';

import { useFormContext } from 'core/states';
import { getDateOptions } from 'core/utils';
import { useGetVerticalMarketByName } from 'corporate/hooks';
import { useGetDtdProject } from 'projects/hooks';

const PROSPECT_CLOSE_CONFIDENCE = 30;
const PROSPECT_VERTICAL_MARKET_NAME = 'Structured Cabling';
// Matches the BE Flow-2 job name (ProspectQuoteService::buildJobName): PHP date('Y-m-d H:i').
const PROSPECT_JOB_NAME_DATE_FORMAT = 'YYYY-MM-DD HH:mm';

// Add-in prospect-quick-quote: reactively prefills quote defaults once the pre-loaded
// bill-to is a PROSPECT (a customer bill-to with no AR number). Values stay editable —
// the ref applies the prefill a single time so later user edits are never overwritten.
// Rendered only inside the iframe in prospect-quick-quote mode (QUOTE only); no-op
// everywhere else, so the in-app form is untouched.
export const ProspectQuickQuoteDefaults = () => {
  const { watch, setValue } = useFormContext();

  const hasAppliedRef = useRef(false);

  const billToLocation = watch('billToLocation');
  const contact = watch('contact');

  const isProspectBillTo = Boolean(billToLocation?.id) && !billToLocation?.accountsReceivableId;

  const { record: dtdProject, isLoading: isDtdProjectLoading } = useGetDtdProject(isProspectBillTo);
  const { record: verticalMarket, isLoading: isVerticalMarketLoading } = useGetVerticalMarketByName(
    PROSPECT_VERTICAL_MARKET_NAME,
    isProspectBillTo
  );

  useEffect(() => {
    // Wait for the async lookups to settle so a single apply picks up project +
    // vertical market; verticalMarket may legitimately be null (no match) -> skip it.
    // The contact gate costs nothing: the prospect bill-to itself is loaded from
    // the contact's defaults, so a prospect bill-to implies the contact is set.
    if (
      hasAppliedRef.current ||
      !isProspectBillTo ||
      !contact?.id ||
      isDtdProjectLoading ||
      isVerticalMarketLoading
    ) {
      return;
    }

    hasAppliedRef.current = true;

    setValue('closeConfidence', PROSPECT_CLOSE_CONFIDENCE);
    setValue('closeDate', getDateOptions()[0]?.value);

    // Mirrors the BE Flow-2 job name: Company + Contact Name + timestamp.
    setValue(
      'jobName',
      [contact.contactGroup?.name, contact.name, dayjs().format(PROSPECT_JOB_NAME_DATE_FORMAT)]
        .filter(Boolean)
        .join(' ')
    );

    // DTD project only auto-defaults for non-alternate users today; prospect quotes
    // are alternate, so force it here.
    if (dtdProject) {
      setValue('project', dtdProject);
    }

    if (verticalMarket) {
      setValue('verticalMarket', verticalMarket);
    }
  }, [
    isProspectBillTo,
    contact,
    isDtdProjectLoading,
    isVerticalMarketLoading,
    dtdProject,
    verticalMarket,
    setValue,
  ]);

  return null;
};
